import { FC, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { primary } from "../../theme/themeColors";
import Login from "./Login/Login";

/* ================================================================== */
/* Geometric Network SVG — pure SVG, no dependencies                 */
/* ================================================================== */

const GeometricNetwork: FC = () => (
  <svg
    viewBox="0 0 520 420"
    width="100%"
    height="100%"
    style={{ position: "absolute", inset: 0, opacity: 0.18, pointerEvents: "none" }}
    preserveAspectRatio="xMidYMid slice"
  >
    {/* Connection lines */}
    {[
      [60, 80, 200, 160], [200, 160, 380, 100], [380, 100, 460, 220],
      [200, 160, 260, 300], [260, 300, 380, 100], [60, 80, 260, 300],
      [460, 220, 380, 340], [260, 300, 380, 340], [380, 340, 200, 400],
      [60, 80, 140, 240], [140, 240, 260, 300], [140, 240, 200, 160],
      [460, 220, 500, 100], [380, 100, 500, 100],
      [200, 400, 60, 350], [60, 350, 60, 80],
    ].map(([x1, y1, x2, y2], i) => (
      <line
        key={i}
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke="rgba(255,255,255,0.6)"
        strokeWidth="1"
      />
    ))}
    {/* Nodes */}
    {[
      [60, 80, 5], [200, 160, 7], [380, 100, 6], [460, 220, 5],
      [260, 300, 8], [380, 340, 5], [200, 400, 4], [140, 240, 5],
      [60, 350, 4], [500, 100, 4],
    ].map(([cx, cy, r], i) => (
      <g key={i}>
        <circle cx={cx} cy={cy} r={(r as number) + 4} fill="rgba(255,255,255,0.06)" />
        <circle cx={cx} cy={cy} r={r} fill="rgba(255,255,255,0.9)" />
      </g>
    ))}
  </svg>
);

/* ================================================================== */
/* Trust signal stats that cycle                                      */
/* ================================================================== */

const STATS = [
  { value: "UGX 2.4B+", label: "Total disbursed" },
  { value: "4,800+",     label: "Active borrowers" },
  { value: "98.2%",      label: "Repayment rate" },
  { value: "320+",       label: "Active investors" },
];

const HEADLINES = [
  {
    headline: "Smart lending,\npowered by data.",
    sub: "Real-time portfolio intelligence for Uganda's fastest-growing P2P lending platform.",
  },
  {
    headline: "Capital flows\nwhere it matters.",
    sub: "Connecting investors to verified borrowers across Uganda with speed and transparency.",
  },
  {
    headline: "Your platform.\nYour control.",
    sub: "Full visibility into every loan, transaction, and user — all from one command centre.",
  },
];

/* ================================================================== */
/* Left Panel                                                         */
/* ================================================================== */

const LeftPanel: FC = () => {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % HEADLINES.length);
        setVisible(true);
      }, 400);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const current = HEADLINES[idx];

  return (
    <Box
      sx={{
        position: "relative",
        width: { xs: "100%", md: "55%" },
        minHeight: { xs: 260, md: "100vh" },
        bgcolor: primary.main,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        overflow: "hidden",
        // Subtle radial glow from top-right
        background: `
          radial-gradient(ellipse 80% 60% at 80% 10%, ${alpha("#6c8fff", 0.22)} 0%, transparent 60%),
          radial-gradient(ellipse 50% 40% at 10% 80%, ${alpha("#1a3a6b", 0.6)} 0%, transparent 60%),
          ${primary.main}
        `,
        order: { xs: 2, md: 1 },
        flexShrink: 0,
      }}
    >
      {/* Geometric background */}
      <GeometricNetwork />

      {/* Diagonal stripe overlay */}
      <Box sx={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 40px,
          rgba(255,255,255,0.015) 40px,
          rgba(255,255,255,0.015) 41px
        )`,
      }} />

      {/* Content */}
      <Box sx={{ position: "relative", zIndex: 1, p: { xs: 3, sm: 5, md: 6 } }}>
        {/* Logo — white version on dark background */}
        <Box sx={{ mb: { xs: 4, md: 8 } }}>
          <Box
            component="img"
            src="/everpesa_logo_white.png"
            alt="Everpesa"
            sx={{
              height: { xs: 28, md: 32 },
              width: "auto",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>

        {/* Headline with transition */}
        <Box
          sx={{
            transition: "opacity 0.4s ease, transform 0.4s ease",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(10px)",
            mb: { xs: 3, md: 5 },
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontSize: { xs: 26, md: 36, lg: 42 },
              lineHeight: 1.15,
              letterSpacing: "-0.03em",
              whiteSpace: "pre-line",
              textShadow: "0 2px 20px rgba(0,0,0,0.15)",
            }}
          >
            {current.headline}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.65)",
              mt: 2,
              fontSize: { xs: 13, md: 15 },
              lineHeight: 1.65,
              maxWidth: 380,
            }}
          >
            {current.sub}
          </Typography>
        </Box>

        {/* Slide dots */}
        <Stack direction="row" spacing={0.75} sx={{ mb: { xs: 4, md: 0 } }}>
          {HEADLINES.map((_, i) => (
            <Box
              key={i}
              onClick={() => setIdx(i)}
              sx={{
                width: i === idx ? 20 : 6,
                height: 6,
                borderRadius: 3,
                bgcolor: i === idx ? "#fff" : "rgba(255,255,255,0.3)",
                cursor: "pointer",
                transition: "all 0.35s ease",
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Stats bar — bottom of left panel */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          borderTop: "1px solid rgba(255,255,255,0.1)",
          px: { xs: 3, sm: 5, md: 6 },
          py: { xs: 2.5, md: 3 },
          display: { xs: "none", md: "block" },
        }}
      >
        <Stack direction="row" spacing={0} sx={{ gap: 0 }}>
          {STATS.map((stat, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                pr: i < STATS.length - 1 ? 3 : 0,
                borderRight: i < STATS.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none",
                mr: i < STATS.length - 1 ? 3 : 0,
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: { md: 18, lg: 21 },
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: 11,
                  mt: 0.25,
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

/* ================================================================== */
/* LoginPortal                                                        */
/* ================================================================== */

export const LoginPortal: FC = () => {
  const location = useLocation().pathname;

  const getComponent = () => {
    switch (location) {
      case "/login":
        return <Login />;
      default:
        return <Login />;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        height: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        overflow: "hidden",
      }}
    >
      {/* Left — branded panel */}
      <LeftPanel />

      {/* Right — form panel */}
      <Box
        sx={{
          width: { xs: "100%", md: "45%" },
          height: { xs: "auto", md: "100vh" },
          minHeight: { xs: "auto", md: "100vh" },
          bgcolor: "background.paper",
          overflow: "auto",
          order: { xs: 1, md: 2 },
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          "&::-webkit-scrollbar": { width: 0 },
        }}
      >
        {getComponent()}
      </Box>
    </Box>
  );
};